import './Library.scss';
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import Book from "../../components/Book/Book";
import SnackbarContext from "../../context/SnackbarContext";
import { API_BASE_URL } from '../../config';

const SORTS = [
    { key: 'recent', label: 'الأحدث' },
    { key: 'views', label: 'الأكثر قراءة' },
    { key: 'rating', label: 'الأعلى تقييمًا' },
];

function sortBooks(list, key) {
    const out = [...list];
    if (key === 'views') return out.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (key === 'rating') {
        // unrated books sink to the bottom; ties broken by how many agree, then by reads
        return out.sort((a, b) => (Number(b.avg_rating) || 0) - (Number(a.avg_rating) || 0)
            || (b.rating_count || 0) - (a.rating_count || 0)
            || (b.view_count || 0) - (a.view_count || 0));
    }
    return out;   // 'recent': the API's order, newest edit first
}

export default function Library() {

    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('');
    const [sort, setSort] = useState('recent');
    const [categories, setCategories] = useState([]);
    const { setSnackbar } = useContext(SnackbarContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const authorFilter = searchParams.get('author') || '';

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const booksUrl = new URL(`${API_BASE_URL}/api/books/library/`);
                if (authorFilter) booksUrl.searchParams.set('author', authorFilter);
                const [booksResp, catsResp] = await Promise.all([
                    fetch(booksUrl.toString()),
                    fetch(`${API_BASE_URL}/api/books/categories/`),
                ]);
                if (!booksResp.ok) throw new Error(`HTTP ${booksResp.status}`);
                const booksData = await booksResp.json();
                if (!cancelled) setBooks(booksData.books ?? []);

                if (catsResp.ok) {
                    const catsData = await catsResp.json();
                    if (!cancelled) setCategories(catsData.categories ?? []);
                }
            } catch (err) {
                if (!cancelled) {
                    setSnackbar({ content: 'تعذر تحميل المكتبة', success: false });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [setSnackbar, authorFilter]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return sortBooks(books.filter(b => {
            if (category && b.category !== category) return false;
            if (!q) return true;
            return (b.title || '').toLowerCase().includes(q)
                || (b.display_author || '').toLowerCase().includes(q)
                || (b.author || '').toLowerCase().includes(q);
        }), sort);
    }, [books, query, category, sort]);

    return (
        <>
        <Navbar />
        <main className='lib-page'>
            <div className='lib-container'>
                <header className='lib-header'>
                    <h1 className='lib-title'>المكتبة</h1>
                    <p className='lib-subtitle'>اكتشف الروايات والقصص التي يكتبها مجتمع راوية</p>
                    {authorFilter && (
                        <div className='lib-author-filter'>
                            <span>الكاتب: <strong>{authorFilter}</strong></span>
                            <button onClick={() => setSearchParams({})}>إزالة الفلترة</button>
                        </div>
                    )}
                </header>

                <div className='lib-search-row'>
                    <input
                        className='lib-search'
                        type='search'
                        placeholder='ابحث بالعنوان أو الكاتب…'
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label='ابحث في المكتبة'
                    />
                    <select
                        className='lib-category'
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        aria-label='تصفية حسب التصنيف'
                    >
                        <option value=''>كل التصنيفات</option>
                        {categories.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                    <select
                        className='lib-category lib-sort'
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        aria-label='ترتيب الكتب'
                    >
                        {SORTS.map(o => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                    </select>
                    <span className='lib-count'>{filtered.length} كتاب</span>
                </div>

                {loading ? (
                    <p className='lib-empty'>جارٍ التحميل…</p>
                ) : filtered.length === 0 ? (
                    <p className='lib-empty'>
                        {books.length === 0 ? 'لا توجد كتب منشورة بعد.' : 'لا توجد نتائج تطابق بحثك.'}
                    </p>
                ) : (
                    <div className='lib-grid'>
                        {filtered.map(book => (
                            <Book
                                key={book.id}
                                id={book.id}
                                title={book.title}
                                author={book.author}
                                displayAuthor={book.display_author}
                                coverImage={book.cover_image}
                                categoryLabel={book.category_label}
                                is_published={book.is_published}
                                publishedAt={book.published_at}
                                updatedAt={book.date_modified}
                                avgRating={book.avg_rating}
                                ratingCount={book.rating_count}
                                viewCount={book.view_count}
                                showBadge={false}
                                authorLink
                                to={`/read/${book.id}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </main>
        <Footer />
        </>
    );
}
